/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: useLoggingSettingsSubmit.ts
 🗓️ Created: 2025-01-XX
 🧩 Description:
    Hook for handling logging settings form submission.
────────────────────────────────────────────────────────────────*/

import { useState, useCallback } from 'react';
import { useAuthStore } from '../../../store/auth';
import { useNotifications } from '../../../contexts/NotificationContext';
import { useI18n } from '../../../hooks/useI18n';
import { createOrUpdateLoggingSettings, deleteLoggingSettings } from '../api/loggingSettingsApi';
import type { LoggingSettings } from '../types';
import { DEFAULT_FILE_LOGGING } from '../types';

export function useLoggingSettingsSubmit(
  formData: LoggingSettings,
  setFormData: React.Dispatch<React.SetStateAction<LoggingSettings>>
) {
  const { user } = useAuthStore();
  const { showSuccess, showError } = useNotifications();
  const { t } = useI18n();
  const [saving, setSaving] = useState(false);

  const handleSave = useCallback(async () => {
    if (!user?.tenant?.slug || !user?.id) return;

    // Validate that at least one log level is selected
    if (!formData.consoleSettings.loglevel || formData.consoleSettings.loglevel.length === 0) {
      showError(t('loggingSettings.console.atLeastOneLevelRequired'));
      return;
    }

    // Ensure we only send one profile with one mapping
    const fileLoggingToSave = {
      ...formData.fileLogging,
      profiles: formData.fileLogging.profiles.length > 0
        ? [{
            ...formData.fileLogging.profiles[0],
            mappings: formData.fileLogging.profiles[0].mappings.length > 0
              ? [formData.fileLogging.profiles[0].mappings[0]]
              : [DEFAULT_FILE_LOGGING.profiles[0].mappings[0]]
          }]
        : [DEFAULT_FILE_LOGGING.profiles[0]]
    };

    try {
      setSaving(true);
      const result = await createOrUpdateLoggingSettings(user.tenant.slug, Number(user.id), {
        console_settings: formData.consoleSettings,
        file_logging: fileLoggingToSave,
        enabled: formData.enabled
      });

      if (result.success) {
        showSuccess(t('loggingSettings.messages.saveSuccess'));
        if (result.data) {
          setFormData(prev => ({
            ...prev,
            ...result.data,
            consoleSettings: result.data!.consoleSettings || prev.consoleSettings,
            fileLogging: result.data!.fileLogging || prev.fileLogging,
            consoleXml: result.data!.consoleXml,
            fileXml: result.data!.fileXml
          }));
        }
      } else {
        throw new Error(result.message || 'Failed to save');
      }
    } catch (error) {
      console.error('Failed to save logging settings:', error);
      showError(t('loggingSettings.messages.saveError'));
    } finally {
      setSaving(false);
    }
  }, [formData, user?.tenant?.slug, user?.id, t, showSuccess, showError, setFormData]);

  const handleDelete = useCallback(async () => {
    if (!user?.tenant?.slug || !user?.id) return;

    try {
      setSaving(true);
      const result = await deleteLoggingSettings(user.tenant.slug, Number(user.id));

      if (result.success) {
        showSuccess(t('loggingSettings.messages.deleteSuccess'));
        // Reset to defaults after deletion
        setFormData(prev => ({
          ...prev,
          consoleSettings: prev.consoleSettings,
          fileLogging: prev.fileLogging,
          enabled: true,
          consoleXml: undefined,
          fileXml: undefined
        }));
      } else {
        throw new Error(result.message || 'Failed to delete');
      }
    } catch (error) {
      console.error('Failed to delete logging settings:', error);
      showError(t('loggingSettings.messages.deleteError'));
    } finally {
      setSaving(false);
    }
  }, [user?.tenant?.slug, user?.id, t, showSuccess, showError, setFormData]);

  return {
    handleSave,
    handleDelete,
    saving
  };
}

